import './ActivityTableFees.scss';

import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormAmountField, FormDataContext } from '@novo-electronique/react-forms';
import classNames from 'classnames';

import { ActivityFees } from '@common/constants/activity-fees';
import { IClaim } from '@common/models/claim';
import { formatCurrency } from '@common/utils/currency';

const feesNames = Object.values(ActivityFees);
const feesApprovedNames = feesNames.map((name) => `${name}Approved`);

export enum ActivityTableFeesType {
  Submission = 'submission',
  Claim = 'claim',
}

interface IProps {
  type: ActivityTableFeesType;
  approvedMode?: boolean;
  paymentIsCreated?: boolean;
}

const ActivityTableFees = ({ type, approvedMode = false, paymentIsCreated = false }: IProps) => {
  const { t } = useTranslation();
  const {
    formik: { setFieldValue },
    values,
  } = useContext(FormDataContext);

  const currentValues = type === ActivityTableFeesType.Claim ? values.claim : values;

  useEffect(() => {
    if (approvedMode) {
      const totalApproved = feesApprovedNames.reduce(
        (total, feesName) => total + (+currentValues[feesName] || 0),
        0,
      );
      setFieldValue('claim.amountApproved', totalApproved);
    }
  }, [approvedMode, ...feesApprovedNames.map((name) => currentValues[name])]);

  const total = feesNames.reduce((total, feesName) => total + (+currentValues[feesName] || 0), 0);
  const className = classNames('cpdActivityTableFees', {
    'cpdActivityTableFees--approved-mode': approvedMode,
  });
  return (
    <table className={className}>
      <tbody>
        {feesNames.map((name) => (
          <tr key={name} className="cpdActivityTableFees__row">
            <td className="cpdActivityTableFees__row__name">{t(`claim.${name}`)}</td>
            <td className="cpdActivityTableFees__row__amount">
              {approvedMode ? (
                formatCurrency(currentValues[name])
              ) : (
                <FormAmountField
                  name={type === ActivityTableFeesType.Claim ? `claim.${name}` : name}
                  width={100}
                />
              )}
            </td>
            {approvedMode && (
              <td className="cpdActivityTableFees__row__amount">
                <FormAmountField
                  name={`claim.${name}Approved`}
                  width={100}
                  max={+currentValues[name]}
                  disabled={paymentIsCreated}
                />
              </td>
            )}
          </tr>
        ))}
        <tr className="cpdActivityTableFees__row cpdActivityTableFees__row--total">
          <td className="cpdActivityTableFees__row__name">{t('claim.total')}</td>
          <td className="cpdActivityTableFees__row__amount">{`${formatCurrency(total)}`}</td>
          {approvedMode && (
            <td className="cpdActivityTableFees__row__amount">{`${formatCurrency(
              currentValues.amountApproved || 0,
            )}`}</td>
          )}
        </tr>
      </tbody>
    </table>
  );
};

export default ActivityTableFees;
